import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="guidebook-public"
// App-like guidebook experience with tab navigation, section drill-down,
// client-side search, and image lightbox.
export default class extends Controller {
  static targets = [
    "tabPanel", "tabButton",
    "sectionList", "sectionButton", "sectionDetail", "detailPanel", "detailEmpty",
    "searchInput", "searchResults", "searchEmpty", "searchNoResults", "searchData",
    "lightbox", "lightboxImage"
  ]

  static values = {
    primaryColor: String
  }

  connect() {
    this.currentTab = "home"
    this.currentSectionIndex = null
    this.isMobile = window.innerWidth < 1024
    this.resizeHandler = () => { this.isMobile = window.innerWidth < 1024 }
    window.addEventListener("resize", this.resizeHandler, { passive: true })
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
  }

  // --- Tab Navigation ---

  switchTab(event) {
    const tab = event.currentTarget.dataset.tab
    this.activateTab(tab)
  }

  getStarted() {
    this.activateTab("info")
  }

  activateTab(tab) {
    this.currentTab = tab

    // Toggle panels
    this.tabPanelTargets.forEach((panel) => {
      panel.classList.toggle("hidden", panel.dataset.tab !== tab)
    })

    // Style active tab button
    this.tabButtonTargets.forEach((btn) => {
      const isActive = btn.dataset.tab === tab
      if (isActive) {
        btn.style.color = `var(--guidebook-primary)`
      } else {
        btn.style.color = ""
        btn.classList.add("text-slate-400")
      }
      btn.classList.toggle("text-slate-400", !isActive)
    })

    // Focus search input when switching to search tab
    if (tab === "search" && this.hasSearchInputTarget) {
      setTimeout(() => this.searchInputTarget.focus(), 100)
    }
  }

  // --- Section Navigation ---

  selectSection(event) {
    const index = parseInt(event.currentTarget.dataset.sectionIndex, 10)
    this.showSection(index)
  }

  showSection(index) {
    this.currentSectionIndex = index

    // Highlight active button
    this.sectionButtonTargets.forEach((btn) => {
      const isActive = parseInt(btn.dataset.sectionIndex, 10) === index
      btn.classList.toggle("bg-slate-50", isActive)
      if (isActive) {
        btn.style.backgroundColor = "color-mix(in srgb, var(--guidebook-primary) 8%, transparent)"
      } else {
        btn.style.backgroundColor = ""
      }
    })

    // Show the selected detail
    this.sectionDetailTargets.forEach((detail) => {
      const isActive = parseInt(detail.dataset.sectionIndex, 10) === index
      detail.classList.toggle("hidden", !isActive)
      if (isActive) {
        detail.classList.add("flex")
      } else {
        detail.classList.remove("flex")
      }
    })

    // Hide the empty state on desktop
    if (this.hasDetailEmptyTarget) {
      this.detailEmptyTarget.classList.add("hidden")
    }

    // Mobile: hide section list, show detail panel full-screen
    if (this.isMobile) {
      if (this.hasSectionListTarget) {
        this.sectionListTarget.classList.add("hidden")
      }
      if (this.hasDetailPanelTarget) {
        this.detailPanelTarget.classList.remove("hidden")
        this.detailPanelTarget.classList.add("flex")
      }
    }
  }

  backToSections() {
    // Mobile: show section list, hide detail panel
    if (this.hasSectionListTarget) {
      this.sectionListTarget.classList.remove("hidden")
    }
    if (this.hasDetailPanelTarget) {
      this.detailPanelTarget.classList.add("hidden")
      this.detailPanelTarget.classList.remove("flex")
    }
    // Hide all section details
    this.sectionDetailTargets.forEach((detail) => {
      detail.classList.add("hidden")
      detail.classList.remove("flex")
    })
    this.currentSectionIndex = null
  }

  // --- Search ---

  search() {
    const query = this.searchInputTarget.value.trim().toLowerCase()

    if (query.length === 0) {
      this.searchEmptyTarget.classList.remove("hidden")
      this.searchResultsTarget.classList.add("hidden")
      this.searchNoResultsTarget.classList.add("hidden")
      return
    }

    this.searchEmptyTarget.classList.add("hidden")

    const sections = JSON.parse(this.searchDataTarget.textContent)
    const results = sections.filter((s) => {
      return s.title.toLowerCase().includes(query) ||
             s.content.toLowerCase().includes(query)
    })

    if (results.length === 0) {
      this.searchResultsTarget.classList.add("hidden")
      this.searchNoResultsTarget.classList.remove("hidden")
      return
    }

    this.searchNoResultsTarget.classList.add("hidden")
    this.searchResultsTarget.classList.remove("hidden")
    this.searchResultsTarget.innerHTML = results.map((result) => {
      const snippet = this.extractSnippet(result.content, query)
      return `
        <button type="button"
                class="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors cursor-pointer"
                data-action="click->guidebook-public#goToSearchResult"
                data-section-index="${result.index}">
          <p class="font-medium text-sm text-slate-900">${this.escapeHtml(result.title)}</p>
          ${snippet ? `<p class="text-xs text-slate-500 mt-1 line-clamp-2">${snippet}</p>` : ""}
        </button>
      `
    }).join("")
  }

  goToSearchResult(event) {
    const index = parseInt(event.currentTarget.dataset.sectionIndex, 10)
    this.activateTab("info")
    // Small delay to let the tab switch render
    setTimeout(() => this.showSection(index), 50)
  }

  extractSnippet(content, query) {
    if (!content) return ""
    const lower = content.toLowerCase()
    const pos = lower.indexOf(query)
    if (pos === -1) return content.substring(0, 120) + (content.length > 120 ? "..." : "")

    const start = Math.max(0, pos - 40)
    const end = Math.min(content.length, pos + query.length + 80)
    let snippet = (start > 0 ? "..." : "") + content.substring(start, end) + (end < content.length ? "..." : "")

    // Highlight the matching text
    const escaped = this.escapeHtml(snippet)
    const escapedQuery = this.escapeHtml(query)
    const regex = new RegExp(`(${escapedQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    return escaped.replace(regex, `<mark class="bg-yellow-200 rounded px-0.5">$1</mark>`)
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  // --- Lightbox ---

  openLightbox(event) {
    const img = event.currentTarget.querySelector("img") || event.currentTarget
    const src = img.src
    if (!src || !this.hasLightboxTarget) return

    this.lightboxImageTarget.src = src
    this.lightboxTarget.classList.remove("hidden")
  }

  closeLightbox(event) {
    // Don't close if clicking on the image itself
    if (event && event.target === this.lightboxImageTarget) return
    if (!this.hasLightboxTarget) return
    this.lightboxTarget.classList.add("hidden")
    this.lightboxImageTarget.src = ""
  }

  closeLightboxOnKey(event) {
    if (event.key === "Escape") {
      this.closeLightbox()
    }
  }
}
